/**
 * ConfirmDialog.tsx — the destructive-action confirmation primitive.
 *
 * Replaces `window.confirm` everywhere (the spec: "dont you dare to use
 * standard browser confirm"). Compact modal: overlay + small panel + message
 * + Cancel / Confirm buttons. Closes on Escape or overlay click = cancel.
 *
 * Imperative-friendly via the `useConfirm` hook: a caller does
 *   const confirm = useConfirm();
 *   if (await confirm({ title, body })) { ...do the destructive thing... }
 * which keeps the call site linear instead of lifting `pendingConfirm` state.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

export interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

export type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/** Read the imperative confirm function. Returns false if no provider — so a
 *  bare call outside the provider fails safe (no destructive action). */
export function useConfirm(): ConfirmFn {
  const fn = useContext(ConfirmContext);
  return useCallback(
    (opts: ConfirmOptions) => (fn ? fn(opts) : Promise.resolve(false)),
    [fn],
  );
}

interface PendingConfirm extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

/** Provider that hosts the dialog. Render once near the app root. */
export function ConfirmProvider({ children }: { children: ReactNode }): JSX.Element {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => {
      setPending({ ...opts, resolve });
    });
  }, []);

  const settle = useCallback(
    (ok: boolean) => {
      pending?.resolve(ok);
      setPending(null);
    },
    [pending],
  );

  // Escape → cancel. Focus the panel so screen readers announce it.
  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') settle(false);
    };
    window.addEventListener('keydown', onKey);
    panelRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [pending, settle]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && (
        <div className="confirm-overlay" onClick={() => settle(false)}>
          <div
            className={`confirm-panel${pending.danger ? ' danger' : ''}`}
            role="alertdialog"
            aria-modal="true"
            aria-label={pending.title}
            tabIndex={-1}
            ref={panelRef}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="confirm-title">{pending.title}</h3>
            {pending.body && <div className="confirm-body">{pending.body}</div>}
            <div className="confirm-buttons">
              <button type="button" className="btn" onClick={() => settle(false)}>
                {pending.cancelLabel ?? 'Cancel'}
              </button>
              <button
                type="button"
                className={`btn${pending.danger ? ' btn-danger' : ' btn-start'}`}
                onClick={() => settle(true)}
                autoFocus
              >
                {pending.confirmLabel ?? (pending.danger ? 'Delete' : 'Confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}
